import { supabase } from '../lib/supabase';
import {
  CompareRow,
  DailyDisconnectRow,
  EventCategory,
  InsightEvent,
  LeagueRow,
  RoomDayRow,
  RouterTimeline,
  RunBand,
  SceneStat,
  TimelineSeries,
} from '../types/insights';

// ---------------------------------------------------------------------------
// Insights data layer — one typed method per telemetry RPC (migration
// 20260704181700_operator_insights_rpcs.sql + telemetry.room_events from
// 20260704181500). Errors follow the database.ts convention: console.error
// and return an empty result so the page renders (empty) instead of crashing.
//
// jsonb-returning RPCs come back from supabase.rpc as already-parsed JSON.
// ---------------------------------------------------------------------------

const telemetry = () => supabase.schema('telemetry');

/** ISO timestamptz (from to_jsonb) -> epoch seconds. */
const isoToEpoch = (iso: string): number => Date.parse(iso) / 1000;

/**
 * Event kinds fetched at every zoom level. Raw WiFi churn (deauth/disassoc/
 * assoc/auth/roam_kick — hundreds per day) is intentionally NOT in this list;
 * it is only fetched under 2 h zoom when the wifi filter chip is on.
 * 'run' and 'scene' are excluded too: those bands come from insights_runs.
 */
const BASE_EVENT_KINDS = [
  'incident_manual',
  'incident_freeze',
  'incident_crash',
  'sample_gap',
  'run_tail_gap',
  'screendoor_suspect',
  'render_degraded',
  'wifi_settings_apply',
  'wifi_wireless_restart',
  'wifi_channel_change',
  'wifi_router_login',
  'wifi_reboot_detected',
  'test_run',
];

const RAW_WIFI_KINDS = ['wifi_deauth', 'wifi_disassoc', 'wifi_assoc', 'wifi_auth', 'wifi_roam_kick'];

/** Map a raw v_room_events kind (+ payload) onto the UI marker category. */
export function categorizeEventKind(kind: string, payload: Record<string, unknown>): EventCategory {
  switch (kind) {
    case 'sample_gap':
    case 'run_tail_gap':
      return payload['confidence'] === 'confirmed' ? 'disconnect' : 'gap';
    case 'screendoor_suspect':
      return 'screendoor_suspect';
    case 'render_degraded':
      return 'render_degraded';
    case 'incident_manual':
      return 'operator_issue';
    case 'incident_freeze':
      return 'freeze';
    case 'incident_crash':
      return 'crash';
    case 'wifi_settings_apply':
    case 'wifi_wireless_restart':
    case 'wifi_reboot_detected':
      return 'router_settings';
    case 'wifi_channel_change':
      return 'channel_change';
    case 'wifi_router_login':
      return 'router_login';
    case 'test_run':
      return 'test_run';
    default:
      return kind.startsWith('wifi_') ? 'wifi' : 'other';
  }
}

/** One raw row of telemetry.room_events (to_jsonb of v_room_events). */
interface RoomEventRow {
  ts: string;
  ts_end: string | null;
  room_id: string | null;
  network_id: string | null;
  pc_instance_id: string | null;
  hmd_mac: string | null;
  run_id: string | null;
  kind: string;
  severity: string | null;
  source: string;
  title: string | null;
  payload: Record<string, unknown> | null;
  source_table: string;
  source_id: string;
}

function mapRoomEventRow(row: RoomEventRow): InsightEvent | null {
  // Run/scene spans are bands (insights_runs), never markers.
  if (row.kind === 'run' || row.kind === 'scene') return null;
  const payload = row.payload ?? {};
  const note = payload['note'];
  const logUrl = payload['log_url'];
  return {
    id: `${row.source_table}:${row.source_id}`,
    src: row.source,
    ts: isoToEpoch(row.ts),
    endTs: row.ts_end ? isoToEpoch(row.ts_end) : null,
    kind: row.kind,
    category: categorizeEventKind(row.kind, payload),
    severity: row.severity ?? 'info',
    pcInstanceId: row.pc_instance_id,
    hmdMac: row.hmd_mac,
    runId: row.run_id,
    title: row.title ?? row.kind,
    detail: typeof note === 'string' ? note : null,
    logUrl: typeof logUrl === 'string' ? logUrl : null,
    payload,
  };
}

/**
 * Map a realtime operations.incidents INSERT payload onto an InsightEvent
 * (same shape v_room_events would produce for it). Used by the today-view
 * subscription so new operator marks appear without a refetch.
 */
export function mapIncidentRow(row: Record<string, any>): InsightEvent {
  const kind = `incident_${row.type}`;
  // Mirror v_room_events' incident payload exactly:
  // i.metadata || jsonb_strip_nulls(jsonb_build_object('log_url', i.log_url)),
  // then derive detail/logUrl the same way mapRoomEventRow does, so a
  // live-marked incident's drawer matches what a refetch would show.
  const payload: Record<string, unknown> = { ...(row.metadata ?? {}) };
  if (row.log_url != null) payload['log_url'] = row.log_url;
  const note = payload['note'];
  const logUrl = payload['log_url'];
  return {
    id: `operations.incidents:${row.id}`,
    src: 'incident',
    ts: isoToEpoch(row.occurred_at ?? row.created_at),
    endTs: null,
    kind,
    category: categorizeEventKind(kind, payload),
    severity: row.severity ?? 'warning',
    pcInstanceId: row.pc_instance_id ?? null,
    hmdMac: null,
    runId: row.run_id ?? null,
    title: row.message ?? row.type,
    detail: typeof note === 'string' ? note : null,
    logUrl: typeof logUrl === 'string' ? logUrl : null,
    payload,
  };
}

class InsightsService {
  /** telemetry.insights_timeline(p_room_id, p_from, p_to, p_bucket_seconds) — jsonb. */
  async getTimeline(
    roomId: string,
    fromIso: string,
    toIso: string,
    bucketSeconds: number
  ): Promise<TimelineSeries[]> {
    const { data, error } = await telemetry().rpc('insights_timeline', {
      p_room_id: roomId,
      p_from: fromIso,
      p_to: toIso,
      p_bucket_seconds: bucketSeconds,
    });

    if (error) {
      console.error('Error fetching insights timeline:', error);
      return [];
    }

    const rows = (data as any[] | null) ?? [];
    return rows.map((r) => ({
      pcInstanceId: r.pc_instance_id as string,
      pcName: (r.pc_name as string | null) ?? 'Unknown PC',
      points: (r.points as (number | null)[][] | null) ?? [],
    }));
  }

  /** telemetry.insights_runs(p_room_id, p_from, p_to) — jsonb run + scene spans. */
  async getRuns(roomId: string, fromIso: string, toIso: string): Promise<RunBand[]> {
    const { data, error } = await telemetry().rpc('insights_runs', {
      p_room_id: roomId,
      p_from: fromIso,
      p_to: toIso,
    });

    if (error) {
      console.error('Error fetching insights runs:', error);
      return [];
    }

    const rows = (data as any[] | null) ?? [];
    return rows.map((r) => ({
      runId: r.run_id as string,
      pcInstanceId: (r.pc_instance_id as string | null) ?? null,
      pcName: (r.pc_name as string | null) ?? null,
      experienceId: (r.experience_id as string | null) ?? null,
      experienceName: (r.experience_name as string | null) ?? 'Unknown',
      experienceVersion: (r.experience_version as string | null) ?? null,
      language: (r.language as string | null) ?? null,
      isBillable: (r.is_billable as boolean | null) ?? null,
      startTs: r.started_at as number,
      endTs: (r.completed_at as number | null) ?? null,
      scenes: (((r.scenes as any[] | null) ?? []) as any[]).map((s) => ({
        sceneIndex: (s.scene_index as number | null) ?? null,
        sceneName: (s.scene_name as string | null) ?? null,
        startTs: s.started_at as number,
        endTs: (s.ended_at as number | null) ?? null,
      })),
    }));
  }

  /** telemetry.insights_router_timeline(p_room_id, p_from, p_to, p_bucket_seconds) — jsonb. */
  async getRouterTimeline(
    roomId: string,
    fromIso: string,
    toIso: string,
    bucketSeconds: number
  ): Promise<RouterTimeline> {
    const { data, error } = await telemetry().rpc('insights_router_timeline', {
      p_room_id: roomId,
      p_from: fromIso,
      p_to: toIso,
      p_bucket_seconds: bucketSeconds,
    });

    if (error) {
      console.error('Error fetching router timeline:', error);
      return { stations: [], radios: [] };
    }

    const obj = (data as any) ?? {};
    return {
      stations: ((obj.stations as any[] | null) ?? []).map((s) => ({
        mac: s.mac as string,
        label: (s.label as string | null) ?? null,
        band: (s.band as string | null) ?? null,
        points: (s.points as (number | null)[][] | null) ?? [],
      })),
      radios: ((obj.radios as any[] | null) ?? []).map((r) => ({
        band: (r.band as string | null) ?? null,
        points: (r.points as (number | null)[][] | null) ?? [],
      })),
    };
  }

  /**
   * telemetry.room_events(p_room_id, p_from, p_to, p_kinds, p_pc_instance_id)
   * — the unified event feed. Raw WiFi churn kinds are only requested when
   * includeRawWifi is true (zoom < 2 h + wifi chip on).
   */
  async getEvents(
    roomId: string,
    fromIso: string,
    toIso: string,
    includeRawWifi: boolean,
    pcInstanceId?: string
  ): Promise<InsightEvent[]> {
    const kinds = includeRawWifi ? [...BASE_EVENT_KINDS, ...RAW_WIFI_KINDS] : BASE_EVENT_KINDS;
    const { data, error } = await telemetry().rpc('room_events', {
      p_room_id: roomId,
      p_from: fromIso,
      p_to: toIso,
      p_kinds: kinds,
      p_pc_instance_id: pcInstanceId ?? null,
    });

    if (error) {
      console.error('Error fetching room events:', error);
      return [];
    }

    const rows = (data as RoomEventRow[] | null) ?? [];
    const events: InsightEvent[] = [];
    for (const row of rows) {
      const ev = mapRoomEventRow(row);
      if (ev) events.push(ev);
    }
    return events;
  }

  /**
   * telemetry.scene_matrix_report(p_experience_name, p_experience_version,
   * p_room_id, p_from, p_to, p_exclude_tests) — the per-scene x version grid.
   * All args passed explicitly (p_from/p_to defaults must not be nulled out).
   */
  async getSceneMatrix(params: {
    experienceName: string;
    experienceVersion?: string | null;
    roomId?: string | null;
    fromIso: string;
    toIso: string;
    excludeTests: boolean;
  }): Promise<SceneStat[]> {
    const { data, error } = await telemetry().rpc('scene_matrix_report', {
      p_experience_name: params.experienceName,
      p_experience_version: params.experienceVersion ?? null,
      p_room_id: params.roomId ?? null,
      p_from: params.fromIso,
      p_to: params.toIso,
      p_exclude_tests: params.excludeTests,
    });

    if (error) {
      console.error('Error fetching scene matrix report:', error);
      return [];
    }
    return (data as SceneStat[] | null) ?? [];
  }

  /** telemetry.pc_league_report(p_room_id, p_from, p_to). */
  async getPcLeague(roomId: string, fromIso: string, toIso: string): Promise<LeagueRow[]> {
    const { data, error } = await telemetry().rpc('pc_league_report', {
      p_room_id: roomId,
      p_from: fromIso,
      p_to: toIso,
    });

    if (error) {
      console.error('Error fetching pc league report:', error);
      return [];
    }
    return (data as LeagueRow[] | null) ?? [];
  }

  /** telemetry.room_day_report(p_room_id, p_from, p_to) — dates as 'YYYY-MM-DD'. */
  async getRoomDays(roomId: string, fromDate: string, toDate: string): Promise<RoomDayRow[]> {
    const { data, error } = await telemetry().rpc('room_day_report', {
      p_room_id: roomId,
      p_from: fromDate,
      p_to: toDate,
    });

    if (error) {
      console.error('Error fetching room day report:', error);
      return [];
    }
    return (data as RoomDayRow[] | null) ?? [];
  }

  /** telemetry.insights_daily_disconnects(p_room_id, p_days) — jsonb. */
  async getDailyDisconnects(roomId: string, days = 30): Promise<DailyDisconnectRow[]> {
    const { data, error } = await telemetry().rpc('insights_daily_disconnects', {
      p_room_id: roomId,
      p_days: days,
    });

    if (error) {
      console.error('Error fetching daily disconnects:', error);
      return [];
    }
    return (data as DailyDisconnectRow[] | null) ?? [];
  }

  /**
   * telemetry.compare_windows_report(p_room_id, p_a_from, p_a_to, p_b_from,
   * p_b_to, p_experience_name, p_experience_version_a, p_experience_version_b).
   */
  async getCompareWindows(params: {
    roomId: string;
    aFromIso: string;
    aToIso: string;
    bFromIso: string;
    bToIso: string;
    experienceName?: string | null;
    experienceVersionA?: string | null;
    experienceVersionB?: string | null;
  }): Promise<CompareRow[]> {
    const { data, error } = await telemetry().rpc('compare_windows_report', {
      p_room_id: params.roomId,
      p_a_from: params.aFromIso,
      p_a_to: params.aToIso,
      p_b_from: params.bFromIso,
      p_b_to: params.bToIso,
      p_experience_name: params.experienceName ?? null,
      p_experience_version_a: params.experienceVersionA ?? null,
      p_experience_version_b: params.experienceVersionB ?? null,
    });

    if (error) {
      console.error('Error fetching compare windows report:', error);
      return [];
    }
    return (data as CompareRow[] | null) ?? [];
  }
}

export const insightsService = new InsightsService();
